/**
 * src/components/spatial/derivedPresentation.ts
 *
 * Properties a map computes about now, which must never become
 * properties an object has.
 *
 * `eventState` established the pattern and is still the only member.
 * It is derived from `metadata.event`'s timestamps at normalize time
 * (`spatialFeatureDerivation.deriveEventState`), stamped onto the
 * GeoJSON written to the source (`SpatialObjectsMapLayer`), and read
 * by paint as `['get', 'eventState']` (`spatialLayerUtils`). Stored
 * geometry and stored metadata are untouched — the transient layer
 * composes over the permanent one, which is exactly the property
 * worth keeping as more of these arrive.
 *
 * The failure mode is subtle and permanent. A derived property that
 * finds its way into a saved object stops being derived: it is now a
 * stale fact, written at whatever moment somebody pressed save, and it
 * will keep colouring that building amber long after the event ended.
 * Nothing would look broken; the map would simply be wrong, and the
 * only record of why would be a timestamp in an audit log.
 *
 * So the registry is explicit and the write path strips it. The rule
 * is enforced by construction rather than by a comment asking the next
 * contributor to remember, because the next contributor will be adding
 * event boundaries, stages and check-in areas, and each of those is
 * another chance to round-trip a temporary truth into a stored one.
 */

/**
 * Every property computed for presentation rather than stored.
 *
 * Add here first, then derive it. A derived property outside this list
 * is one the write path cannot protect.
 */
export const DERIVED_PRESENTATION_KEYS: readonly string[] = Object.freeze([
  'eventState',
  // What an operator decided, combined with the clock, as a single
  // presentable answer. Derived rather than stored for the reason
  // above and one more: a cancelled event still has perfectly good
  // timestamps, so anything painting from `startsAt` alone paints it
  // as live. `eventLifecycle` is the status that produced the state.
  'eventLifecycle',
  // Whether this event is one part of a larger one. Read from the
  // parent link at normalize time so a multi-part festival can be
  // drawn as a group without any member storing its own grouping.
  'eventIsPart',
  // A count of what is on at a place right now. Entirely a function
  // of other objects, so storing it on this one would be stale the
  // moment any of them changed.
  'eventCount',
  // Whether this is the object currently being talked about — the one
  // an event panel opened, or a list hovered. The most obviously
  // transient member of the list: it is true for as long as somebody
  // is looking and belongs to a session rather than to a building.
  // Registered here so it is stripped on the way to a save like the
  // rest, rather than becoming a building that is permanently
  // highlighted for everybody.
  'eventHighlighted',
]);

const DERIVED = new Set(DERIVED_PRESENTATION_KEYS);

export function isDerivedPresentationKey(key: string): boolean {
  return DERIVED.has(key);
}

/**
 * The same object with every derived property removed.
 *
 * Returns the input untouched when there is nothing to strip, so the
 * common case allocates nothing and a caller can use it freely on a
 * hot path.
 */
export function stripDerivedPresentation<T extends Record<string, unknown>>(
  props: T | null | undefined,
): T {
  if (!props || typeof props !== 'object') return {} as T;

  const present = Object.keys(props).filter(isDerivedPresentationKey);
  if (!present.length) return props;

  const clean: Record<string, unknown> = { ...props };
  for (const key of present) delete clean[key];
  return clean as T;
}
